<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import MinerHistoryDialog from '@/components/rc/miner-repairs/MinerHistoryDialog.vue'
import MinerPartCard from '@/components/rc/miner-repairs/MinerPartCard.vue'
import RepairDrawerHeader from '@/components/rc/miner-repairs/repairs-sidebar/RepairDrawerHeader.vue'
import RepairDrawerMinerBody from '@/components/rc/miner-repairs/repairs-sidebar/RepairDrawerMinerBody.vue'
import RepairDrawerPartBody from '@/components/rc/miner-repairs/repairs-sidebar/RepairDrawerPartBody.vue'
import RepairSectionHeader from '@/components/rc/miner-repairs/RepairSectionHeader.vue'
import SparePartSelectionDialog from '@/components/rc/miner-repairs/SparePartSelectionDialog.vue'
import RepairStressTestsDialog from '@/components/rc/miner-repairs/stress-test/RepairStressTestsDialog.vue'
import { provideSidebarContext } from '@/composables/rc/drawer-context'
import { useMinerDetails } from '@/composables/rc/miner-details'
import { useMinerSparePartReplacements } from '@/composables/rc/miner-spare-part-replacements'
import { useOIDCStore } from '@/stores/oidc.store'
import type { IMinerPartAllocationForm, IMinerPartDeleteForm, IMinerPartMoveToInventoryForm } from '@/types/rc/forms'
import { minerDetailsParts as rows, HashboardParts } from '@/types/rc/miners'
import { IndividualPart, IndividualPartToNumber, PartType, partTypes } from '@/types/rc/miners/PartType'
import BreadcrumbsViewLayout, { type BreadcrumbItem } from '@/views/layouts/BreadcrumbsViewLayout.vue'
import { Icon } from '@iconify/vue'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, toRefs } from 'vue'
import { useRouter } from 'vue-router'

enum MINERS_ROUTES {
  DASHBOARD = '/rc/miners/dashboard',
  WORK_ORDERS = '/rc/work-orders/',
}

const router = useRouter()

const oidcStore = useOIDCStore()
const { isRcAppManagerUser } = storeToRefs(oidcStore)

const props = defineProps<{ minerId: number }>()
const { minerId } = toRefs(props)

const { minerDetailsQuery } = useMinerDetails(minerId)

// Drawer state
const drawerOpen = ref(true)
const selectedPart = ref<IndividualPart>(IndividualPart.Miner)

// Provide bindings to minerId and selectedPart to all subtree
provideSidebarContext({ minerId, selectedPart })

const setPart = (part: IndividualPart) => {
  selectedPart.value = part
}

const isStressTestModalOpen = ref(false)
const isMinerHistoryDialogOpen = ref(false)
const isSparePartSelectionDialogOpen = ref<boolean>(false)

const showStressTestsModal = () => {
  isStressTestModalOpen.value = true
}

const partsTableRef = ref()

const {
  data: minerDetails,
  isRefetching: minerDetailsRefetchingState,
  isError: minerDetailsErrorState,
  error: minerDetailsFetchError,
  refetch: refetchMinerDetails,
} = minerDetailsQuery

onMounted(refetchMinerDetails)

const buildBreadcrumbItems = computed((): BreadcrumbItem[] => {
  if (!minerDetails.value) {
    return []
  }

  const minersOrigin = router.options.history.state.back?.toString() ?? ''
  const minersBreadcrumbsLabel = minersOrigin.includes(MINERS_ROUTES.DASHBOARD) ? 'Miners Dashboard' : 'Miners'
  const minersOriginRedirect = minersOrigin.includes(MINERS_ROUTES.DASHBOARD)
    ? { name: 'rc-miners-dashboard', query: { workOrderNumber: minerDetails.value.work_order_number } }
    : { name: 'rc-work-order-dashboard-miners', params: { number: minerDetails.value.work_order_number } }

  return [
    {
      displayName: minerDetails.value.customer.name,
      route: isRcAppManagerUser.value
        ? { name: 'rc-customer', params: { customerId: minerDetails.value.customer.id } }
        : undefined,
    },
    {
      displayName: minerDetails.value.work_order_number,
      route: {
        name: 'rc-work-order-dashboard',
        params: { number: minerDetails.value.work_order_number },
      },
    },
    {
      displayName: minersBreadcrumbsLabel,
      route: minersOriginRedirect,
    },
    {
      displayName: minerDetails.value.sn,
    },
  ]
})

const minerPartRows = computed(() => {
  if (minerDetails.value?.hashboard_only) {
    return rows.filter((part: IndividualPart) => HashboardParts.includes(part))
  }
  return rows
})

const {
  usePartDeleteMutation,
  usePartAllocateMutation,
  usePartMoveToInventoryMutation,
  usePartMoveBackFromInventoryMutation,
} = useMinerSparePartReplacements(minerId)
const { mutateAsync: deletePartAsyncMutation } = usePartDeleteMutation()
const { mutateAsync: allocatePartAsyncMutation } = usePartAllocateMutation()
const { mutateAsync: movePartToInventoryAsyncMutation } = usePartMoveToInventoryMutation()
const { mutateAsync: movePartBackFromInventoryAsyncMutation } = usePartMoveBackFromInventoryMutation()

const handlePartDelete = (partID: number, partType: PartType) => {
  const deleteForm: IMinerPartDeleteForm = {
    part_id: partID,
    part_type: partType,
  }
  DeleteConfirmationModal(
    'Mark part as missing',
    'Are you sure you want to mark this part as missing? This will remove the part from the miner along with all its data',
    deleteForm,
    deletePartAsyncMutation
  )
}

const handlePartMoveToInventory = (partID: number, partType: PartType) => {
  const moveToInventoryForm: IMinerPartMoveToInventoryForm = {
    part_id: partID,
    part_type: partType,
  }

  DeleteConfirmationModal(
    'Move part to inventory',
    'Moving a part to the inventory will show it as a spare part in<br>the inventory. The part will be also kept in the miner for<br>informational purposes as a read-only part.<br><br>Are you sure you want to proceed?',
    moveToInventoryForm,
    movePartToInventoryAsyncMutation,
    () => {},
    'Move'
  )
}

const handlePartMoveBackFromInventory = (partID: number, partType: PartType) => {
  const moveBackFromInventoryForm: IMinerPartMoveToInventoryForm = {
    part_id: partID,
    part_type: partType,
  }

  DeleteConfirmationModal(
    'Move part back from inventory',
    'Moving a part back from inventory will make it an actionable part in the miner again. The part will also be removed from the inventory.<br><br>Are you sure you want to proceed?',
    moveBackFromInventoryForm,
    movePartBackFromInventoryAsyncMutation,
    () => {},
    'Move back'
  )
}

const handleSparePartSelect = async (sparePartID: number) => {
  const selectedPartType = partTypes[selectedPart.value]

  const form: IMinerPartAllocationForm = {
    spare_part_id: sparePartID,
    part_type: selectedPartType,
  }

  if (selectedPartType == PartType.Hashboard || selectedPartType == PartType.Fan) {
    form.part_number = IndividualPartToNumber[selectedPart.value]!
  }

  try {
    await allocatePartAsyncMutation(form)
  } catch {
    // Handled by mutation, ignore
  } finally {
    isSparePartSelectionDialogOpen.value = false
  }
}
</script>

<template>
  <BreadcrumbsViewLayout :items="buildBreadcrumbItems">
    <rl-error-result v-if="minerDetailsErrorState && !minerDetailsRefetchingState" :error="minerDetailsFetchError">
      <el-button @click="() => refetchMinerDetails()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>

    <!-- Dialogs -->
    <RepairStressTestsDialog
      v-model="isStressTestModalOpen"
      :miner-id="props.minerId"
      @close="isStressTestModalOpen = false"
    />
    <MinerHistoryDialog
      v-model="isMinerHistoryDialogOpen"
      :miner-id="props.minerId"
      @close="isMinerHistoryDialogOpen = false"
    />
    <SparePartSelectionDialog
      v-if="minerDetails && partTypes[selectedPart] != PartType.Miner"
      v-model="isSparePartSelectionDialogOpen"
      title="Allocate Spare Part"
      dialog-text="Choose a spare part to assign to this miner part"
      :part-type="partTypes[selectedPart]"
      :customer-id="minerDetails.customer.id"
      @spare-part-selected="handleSparePartSelect"
      @close="isSparePartSelectionDialogOpen = false"
    />

    <!-- Drawer -->
    <el-drawer
      v-if="minerDetails?.id"
      v-model="drawerOpen"
      modal-class="drawer-modal"
      :close-on-press-escape="false"
      :lock-scroll="false"
      :modal="false"
      :with-header="false"
    >
      <!-- Header + miner/part -->
      <RepairDrawerHeader v-if="selectedPart" :miner-id="props.minerId" :selected-part="selectedPart" />

      <el-divider class="my-6" />

      <RepairDrawerMinerBody
        v-if="selectedPart == IndividualPart.Miner"
        :miner-id="props.minerId"
        :work-order-number="minerDetails.work_order_number"
        @show-all-stress-tests="showStressTestsModal"
      />
      <RepairDrawerPartBody
        v-if="selectedPart && selectedPart != IndividualPart.Miner"
        :customer-id="minerDetails.customer.id"
      />
    </el-drawer>

    <!-- Base page header + parts table -->
    <template v-if="minerDetails">
      <main class="main-container contracted" :v-loading="minerDetailsRefetchingState">
        <RepairSectionHeader :miner-details="minerDetails" @open-history-dialog="isMinerHistoryDialogOpen = true" />

        <el-divider />

        <div ref="partsTableRef" class="parts-table">
          <MinerPartCard
            :miner-id="minerId"
            :selected="selectedPart == IndividualPart.Miner"
            :part="IndividualPart.Miner"
            @open-drawer="selectedPart = IndividualPart.Miner"
          />
          <MinerPartCard
            v-for="part in minerPartRows"
            :key="part"
            :miner-id="minerId"
            :selected="selectedPart == part"
            :part="part"
            @open-drawer="setPart"
            @open-spare-part-selection-dialog="isSparePartSelectionDialogOpen = true"
            @delete-part="handlePartDelete"
            @move-to-inventory="handlePartMoveToInventory"
            @move-back-from-inventory="handlePartMoveBackFromInventory"
          />
        </div>
      </main>
    </template>
  </BreadcrumbsViewLayout>
</template>

<style scoped lang="scss">
:deep() {
  // Disable modal overlay
  .drawer-modal {
    pointer-events: none;
  }

  .el-drawer {
    pointer-events: all;

    // Design @Figma
    background-color: var(--fill-secondary);
    width: 520px !important;

    &__body {
      padding: var(--spacing-spacing-lg);
      display: flex;
      flex-direction: column;
    }

    &__sr-focus {
      display: none;
    }
  }
}

.main-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-lg);
  width: 970px;
  overflow-x: scroll;
}

.main-container.contracted {
  width: calc(100% - 520px);
}

.parts-table {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-ss);
}
</style>
